import { db, now } from '../db/dexie';
import { supabase } from './supabase';

const BUCKET = 'attachments';

export interface BlobResult {
  uploaded: number;
  downloaded: number;
  /** Rows whose binary could not be fetched this cycle. Retried next time. */
  failed: number;
}

/**
 * Image binaries are immutable and content-addressed — an attachment is never
 * edited, only created or deleted. So blobs need no conflict resolution at all;
 * only the metadata rows participate in last-write-wins.
 */
export async function syncBlobs(userId: string): Promise<BlobResult> {
  const result: BlobResult = { uploaded: 0, downloaded: 0, failed: 0 };
  if (!supabase) return result;

  const rows = (await db.attachments.toArray()).filter((a) => !a.deleted);

  // ── Upload anything that has not reached Storage ────────
  for (const row of rows.filter((a) => a.uploaded === 0)) {
    const local = await db.blobs.get(row.id);
    // No local blob and not uploaded means the binary is simply gone — the row
    // is a tombstone in all but name. Skip it rather than retrying forever.
    if (!local) continue;

    const path = `${userId}/${row.id}.webp`;
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, local.data, { contentType: row.mime_type, upsert: true });

    if (error) {
      result.failed += 1;
      continue;
    }

    // The path only becomes canonical once the bytes are actually there.
    await db.attachments.update(row.id, {
      storage_path: path,
      uploaded: 1,
      updated_at: now(),
      dirty: 1,
    });
    result.uploaded += 1;
  }

  // ── Download anything with no local copy ────────────────
  // Eager, not lazy: once compressed to WebP the whole library is small, and
  // eager means an offline phone already has every image it might open.
  for (const row of rows.filter((a) => a.uploaded === 1)) {
    if (await db.blobs.get(row.id)) continue;

    const { data, error } = await supabase.storage.from(BUCKET).download(row.storage_path);
    if (error || !data) {
      result.failed += 1;
      continue;
    }

    await db.blobs.put({ attachment_id: row.id, data });
    result.downloaded += 1;
  }

  return result;
}

/** Removes the stored object behind a tombstoned attachment. */
export async function purgeBlob(attachmentId: string, storagePath: string): Promise<void> {
  await db.blobs.delete(attachmentId);
  if (supabase) await supabase.storage.from(BUCKET).remove([storagePath]);
}
